import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  TrendingUp, 
  Wallet, 
  Clock, 
  FolderOpen,
  AlertTriangle,
  ArrowRight,
  BarChart3,
  DollarSign,
  Users,
  Target,
  Skull,
  Percent,
  Layers,
  ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { CurrencyDisplay } from '../components/ui/currency-display';
import { analyticsApi, ledgerApi, reportsApi } from '../lib/api';
import { formatCurrency, getStatusColor, getAgingLevel } from '../lib/utils';
import { StatusBadge } from '../components/StatusBadge';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LineChart, Line, CartesianGrid, PieChart, Pie } from 'recharts';

export const Dashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [ledgerSummary, setLedgerSummary] = useState(null);
  const [ytdSummary, setYtdSummary] = useState(null);
  const [capitalByParty, setCapitalByParty] = useState(null);
  const [pipelineInsights, setPipelineInsights] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [dealPipeline, setDealPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillDown, setDrillDown] = useState(null); // { title, party, category, projects, color }

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [analyticsRes, ledgerRes, ytdRes, capitalRes, pipelineRes, forecastRes, dealPipelineRes] = await Promise.all([
        analyticsApi.getDashboard(),
        ledgerApi.getSummary(),
        reportsApi.getYtdSummary(new Date().getFullYear()),
        analyticsApi.getCapitalByParty().catch(() => ({ data: null })),
        analyticsApi.getPipelineInsights().catch(() => ({ data: null })),
        analyticsApi.getCapitalForecast().catch(() => ({ data: null })),
        analyticsApi.getDealPipeline().catch(() => ({ data: null }))
      ]);
      setAnalytics(analyticsRes.data);
      setLedgerSummary(ledgerRes.data);
      setYtdSummary(ytdRes.data);
      setCapitalByParty(capitalRes.data);
      setPipelineInsights(pipelineRes.data);
      setForecast(forecastRes.data);
      setDealPipeline(dealPipelineRes.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const statusChartData = analytics?.status_counts 
    ? Object.entries(analytics.status_counts).map(([status, count]) => ({
        name: status.replace('_', ' '),
        value: count,
        status: status
      })).filter(d => d.value > 0)
    : [];

  // Color mapping for status - Dead deals get red
  const getStatusColor = (status) => {
    const colorMap = {
      'Under_Contract': '#8B5CF6', // Purple
      'Owned': '#1A2F23',          // Forest
      'Listed': '#06B6D4',         // Cyan
      'Subdividing': '#F97316',    // Orange
      'Under_Contract_Sell': '#A855F7', // Violet
      'Sold': '#22C55E',           // Green
      'Completed': '#22C55E',      // Green (same as Sold)
      'Note_Held': '#10B981',      // Emerald
      'Dead': '#EF4444'            // Red
    };
    return colorMap[status] || '#6B7280';
  };

  const CHART_COLORS = ['#1A2F23', '#C68E5D', '#4B5563', '#3B82F6', '#8B5CF6', '#06B6D4', '#22C55E', '#F97316'];

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold">Dashboard</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Land Finder Operating System Overview</p>
        </div>
        <Link to="/pipeline">
          <Button className="bg-forest-500 hover:bg-forest-600 text-white rounded-sm uppercase text-xs font-semibold tracking-wide w-full sm:w-auto" data-testid="view-pipeline-btn">
            View Pipeline <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
        <Card className="rounded-sm border" data-testid="stat-active-projects">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground font-medium">Active Projects</p>
                <p className="text-2xl sm:text-3xl font-heading font-bold mt-1">{analytics?.active_projects || 0}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground mt-1 hidden sm:block">Excludes sold & dead</p>
              </div>
              <div className="p-1.5 sm:p-2 bg-muted rounded-sm">
                <FolderOpen className="w-4 h-4 sm:w-5 sm:h-5 text-forest-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-sm border" data-testid="stat-sold-projects">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground font-medium">Sold Projects</p>
                <p className="text-2xl sm:text-3xl font-heading font-bold mt-1 text-emerald-600">{analytics?.sold_projects || 0}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground mt-1 hidden sm:block">Cash & note held</p>
              </div>
              <div className="p-1.5 sm:p-2 bg-muted rounded-sm">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-sm border" data-testid="stat-capital-deployed">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground font-medium">Current Inventory (Purchased)</p>
                <CurrencyDisplay amount={analytics?.capital_deployed} size="2xl" className="mt-1 font-heading" />
                <p className="text-[10px] sm:text-xs text-muted-foreground mt-1 hidden sm:block">Acquisition cost of active inventory</p>
              </div>
              <div className="p-1.5 sm:p-2 bg-terracotta-50 rounded-sm">
                <Wallet className="w-4 h-4 sm:w-5 sm:h-5 text-terracotta" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-sm border" data-testid="stat-dead-projects">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground font-medium">Dead Deals</p>
                <p className="text-2xl sm:text-3xl font-heading font-bold mt-1 text-red-600">{analytics?.dead_projects || 0}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground mt-1 hidden sm:block">
                  {pipelineInsights ? `${pipelineInsights.conversion_funnel.kill_rate}% kill rate` : 'Failed deals'}
                </p>
              </div>
              <div className="p-1.5 sm:p-2 bg-muted rounded-sm">
                <Skull className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* YTD Financial Summary */}
      {ytdSummary?.summary && (
        <Card className="rounded-sm border" data-testid="ytd-financial-summary">
          <CardHeader className="pb-2">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                {new Date().getFullYear()} Year-to-Date Financial Summary
              </CardTitle>
              <Link to="/financial-reports">
                <Button variant="outline" size="sm" className="rounded-sm text-xs w-full sm:w-auto">
                  View Full Reports <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 sm:gap-4">
              <div className="p-4 bg-white rounded-sm border">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Total Revenue</p>
                <CurrencyDisplay amount={ytdSummary.summary.total_revenue || 0} size="xl" className="font-heading text-emerald-600" />
              </div>
              <div className="p-4 bg-white rounded-sm border">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Gross Profit</p>
                <CurrencyDisplay amount={ytdSummary.summary.net_profit || 0} size="xl" className="font-heading text-emerald-600" />
                <p className="text-[10px] text-muted-foreground mt-0.5">Before commissions</p>
              </div>
              <div className="p-4 bg-white rounded-sm border">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Commissions</p>
                <CurrencyDisplay amount={-(ytdSummary.summary.total_commissions_paid || 0)} size="xl" className="font-heading text-red-600" />
              </div>
              <div className="p-4 bg-white rounded-sm border border-forest-200">
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Net Profit</p>
                <CurrencyDisplay amount={ytdSummary.summary.profit_after_commissions || 0} size="xl" className="font-heading text-forest-700" />
              </div>
              <div className="p-4 bg-white rounded-sm border">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Deals Closed</p>
                <p className="text-xl font-heading font-bold font-mono">
                  {ytdSummary.summary.deals_closed || 0}
                </p>
              </div>
              <div className="p-4 bg-white rounded-sm border">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Notes Receivable</p>
                <CurrencyDisplay amount={ytdSummary.active_portfolio?.notes_receivable || 0} size="xl" className="font-heading" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Deal Pipeline Funnel */}
      {dealPipeline?.stages && dealPipeline.total_promoted > 0 && (
        <DealPipelineFunnel data={dealPipeline} />
      )}

      {/* Capital by Party Summary */}
      {capitalByParty && capitalByParty.totals?.total_net_cost_basis > 0 && (
        <Card className="rounded-sm border" data-testid="capital-by-party-summary">
          <CardHeader className="pb-2">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2">
                <Users className="w-4 h-4" />
                Cost Basis by Party
              </CardTitle>
              <Link to="/reports">
                <Button variant="outline" size="sm" className="rounded-sm text-xs w-full sm:w-auto">
                  View Details <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </Link>
            </div>
            <p className="text-xs text-muted-foreground">
              Active projects only ({capitalByParty.active_projects_count} projects, {capitalByParty.jv_projects_count} with JV) — Click any row for project-level breakdown
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Investor Column */}
              <div className="p-4 bg-white rounded-sm border">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-blue-700 mb-3">
                  Investor / Partner
                  {capitalByParty.jv_projects_count > 0 && (
                    <span className="ml-1 text-xs font-normal text-muted-foreground">({capitalByParty.jv_projects_count} JV deals)</span>
                  )}
                </h4>
                <div className="space-y-1">
                  <CostBasisRow
                    label="Funded Capital"
                    amount={capitalByParty.investor?.total_capital_contributed || 0}
                    onClick={() => setDrillDown({
                      title: 'Investor — Funded Capital',
                      projects: capitalByParty.investor?.project_details || [],
                      columns: ['jv_capital', 'purchase_price', 'expenses', 'emd'],
                      color: 'blue',
                    })}
                    data-testid="investor-funded-capital"
                  />
                  {capitalByParty.investor?.reimbursements > 0 && (
                    <CostBasisRow
                      label="- Reimbursements"
                      amount={capitalByParty.investor.reimbursements}
                      negative
                      onClick={() => setDrillDown({
                        title: 'Investor — Reimbursements',
                        projects: (capitalByParty.investor?.project_details || []).filter(p => p.reimbursements > 0),
                        columns: ['reimbursements'],
                        color: 'red',
                      })}
                      data-testid="investor-reimbursements"
                    />
                  )}
                  <div className="flex justify-between pt-2 border-t border-blue-200 font-semibold">
                    <span className="text-sm">Net Cost Basis</span>
                    <CurrencyDisplay amount={capitalByParty.investor?.net_cost_basis || 0} className="text-blue-700" />
                  </div>
                </div>
              </div>
              
              {/* Land Finder Column */}
              <div className="p-4 bg-terracotta-50 rounded-sm border border-terracotta-100">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-terracotta-700 mb-3">
                  Land Finder
                  {capitalByParty.non_jv_projects_count > 0 && (
                    <span className="ml-1 text-xs font-normal text-muted-foreground">({capitalByParty.non_jv_projects_count} self-funded)</span>
                  )}
                </h4>
                <div className="space-y-1">
                  <CostBasisRow
                    label="Total Capital Contributed"
                    amount={capitalByParty.land_finder?.total_capital_contributed || 0}
                    onClick={() => setDrillDown({
                      title: 'Land Finder — Capital Contributed',
                      projects: capitalByParty.land_finder?.project_details || [],
                      columns: ['purchase_price', 'expenses', 'emd'],
                      color: 'terracotta',
                    })}
                    data-testid="lf-capital-contributed"
                  />
                  {capitalByParty.land_finder?.reimbursements > 0 && (
                    <CostBasisRow
                      label="- Reimbursements"
                      amount={capitalByParty.land_finder.reimbursements}
                      negative
                      onClick={() => setDrillDown({
                        title: 'Land Finder — Reimbursements',
                        projects: (capitalByParty.land_finder?.project_details || []).filter(p => p.reimbursements > 0),
                        columns: ['reimbursements'],
                        color: 'red',
                      })}
                      data-testid="lf-reimbursements"
                    />
                  )}
                  <div className="flex justify-between pt-2 border-t border-terracotta-200 font-semibold">
                    <span className="text-sm">Net Cost Basis</span>
                    <CurrencyDisplay amount={capitalByParty.land_finder?.net_cost_basis || 0} className="text-terracotta-700" />
                  </div>
                </div>
              </div>
              
              {/* Combined Total Column */}
              <div className="p-4 bg-white rounded-sm border">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-forest-700 mb-3">Combined Total</h4>
                <div className="space-y-1">
                  <CostBasisRow
                    label="Total Capital Contributed"
                    amount={capitalByParty.totals?.total_capital_contributed || 0}
                    onClick={() => setDrillDown({
                      title: 'Combined — All Capital Contributed',
                      projects: [
                        ...(capitalByParty.investor?.project_details || []).map(p => ({ ...p, party: 'Investor' })),
                        ...(capitalByParty.land_finder?.project_details || []).map(p => ({ ...p, party: 'Land Finder' })),
                      ],
                      columns: ['jv_capital', 'purchase_price', 'expenses', 'emd'],
                      color: 'forest',
                      showParty: true,
                    })}
                    data-testid="combined-capital"
                  />
                  {capitalByParty.totals?.total_reimbursements > 0 && (
                    <CostBasisRow
                      label="- Reimbursements"
                      amount={capitalByParty.totals.total_reimbursements}
                      negative
                      onClick={() => setDrillDown({
                        title: 'Combined — All Reimbursements',
                        projects: [
                          ...(capitalByParty.investor?.project_details || []).filter(p => p.reimbursements > 0).map(p => ({ ...p, party: 'Investor' })),
                          ...(capitalByParty.land_finder?.project_details || []).filter(p => p.reimbursements > 0).map(p => ({ ...p, party: 'Land Finder' })),
                        ],
                        columns: ['reimbursements'],
                        color: 'red',
                        showParty: true,
                      })}
                      data-testid="combined-reimbursements"
                    />
                  )}
                  <div className="flex justify-between pt-2 border-t border-forest-300 font-semibold">
                    <span className="text-sm">Total Cost Basis</span>
                    <CurrencyDisplay amount={capitalByParty.totals?.total_net_cost_basis || 0} className="text-forest-700" />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cost Basis Drill-Down Dialog */}
      <CostBasisDrillDown
        open={!!drillDown}
        onOpenChange={(open) => !open && setDrillDown(null)}
        data={drillDown}
      />

      {/* Deal Conversion Funnel & Pipeline Insights */}
      {pipelineInsights && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Conversion Funnel */}
          <Card className="rounded-sm border" data-testid="conversion-funnel">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2">
                <Target className="w-4 h-4" />
                Deal Conversion Funnel
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {/* Total Deals Bar */}
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Total Deals</span>
                    <span className="font-mono font-semibold">{pipelineInsights.conversion_funnel.total_deals}</span>
                  </div>
                  <div className="w-full h-6 bg-muted rounded-sm overflow-hidden flex">
                    {pipelineInsights.conversion_funnel.active > 0 && (
                      <div 
                        className="h-full bg-blue-500 flex items-center justify-center text-xs text-white font-medium"
                        style={{ width: `${(pipelineInsights.conversion_funnel.active / pipelineInsights.conversion_funnel.total_deals) * 100}%`, minWidth: pipelineInsights.conversion_funnel.active > 0 ? '20px' : '0' }}
                      >
                        {pipelineInsights.conversion_funnel.active}
                      </div>
                    )}
                    {pipelineInsights.conversion_funnel.sold > 0 && (
                      <div 
                        className="h-full bg-emerald-500 flex items-center justify-center text-xs text-white font-medium"
                        style={{ width: `${(pipelineInsights.conversion_funnel.sold / pipelineInsights.conversion_funnel.total_deals) * 100}%`, minWidth: '20px' }}
                      >
                        {pipelineInsights.conversion_funnel.sold}
                      </div>
                    )}
                    {pipelineInsights.conversion_funnel.dead > 0 && (
                      <div 
                        className="h-full bg-red-500 flex items-center justify-center text-xs text-white font-medium"
                        style={{ width: `${(pipelineInsights.conversion_funnel.dead / pipelineInsights.conversion_funnel.total_deals) * 100}%`, minWidth: '20px' }}
                      >
                        {pipelineInsights.conversion_funnel.dead}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Legend */}
                <div className="flex gap-4 text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 bg-blue-500 rounded-sm" />
                    <span>Active ({pipelineInsights.conversion_funnel.active})</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 bg-emerald-500 rounded-sm" />
                    <span>Sold ({pipelineInsights.conversion_funnel.sold})</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 bg-red-500 rounded-sm" />
                    <span>Dead ({pipelineInsights.conversion_funnel.dead})</span>
                  </div>
                </div>

                {/* Rate Cards */}
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <div className="p-3 bg-white rounded-sm border text-center">
                    <p className="text-2xl font-mono font-bold text-emerald-600">{pipelineInsights.conversion_funnel.conversion_rate}%</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Win Rate</p>
                  </div>
                  <div className="p-3 bg-white rounded-sm border text-center">
                    <p className="text-2xl font-mono font-bold text-red-600">{pipelineInsights.conversion_funnel.kill_rate}%</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Kill Rate</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Deal Size Distribution */}
          <Card className="rounded-sm border" data-testid="deal-size-distribution">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2">
                <Layers className="w-4 h-4" />
                Deal Size Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                const sizes = pipelineInsights.deal_size_distribution;
                const sizeData = [
                  { name: '<$25K', value: sizes.under_25k, color: '#6B7280' },
                  { name: '$25-50K', value: sizes['25k_50k'], color: '#3B82F6' },
                  { name: '$50-100K', value: sizes['50k_100k'], color: '#8B5CF6' },
                  { name: '$100-250K', value: sizes['100k_250k'], color: '#C68E5D' },
                  { name: '$250K+', value: sizes.over_250k, color: '#1A2F23' },
                ].filter(d => d.value > 0);
                
                return (
                  <div className="flex items-center gap-4">
                    <ResponsiveContainer width="50%" height={160}>
                      <PieChart>
                        <Pie
                          data={sizeData}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={70}
                          dataKey="value"
                          stroke="none"
                        >
                          {sizeData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => `${value} deals`} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="space-y-2 flex-1">
                      {sizeData.map((item) => (
                        <div key={item.name} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: item.color }} />
                            <span className="text-muted-foreground">{item.name}</span>
                          </div>
                          <span className="font-mono font-semibold">{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Monthly Profit Trend */}
      {pipelineInsights?.monthly_profit_trend && (
        <Card className="rounded-sm border" data-testid="monthly-profit-trend">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Monthly Revenue & Profit Trend (12 Months)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pipelineInsights.monthly_profit_trend.some(m => m.revenue > 0 || m.profit > 0) ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={pipelineInsights.monthly_profit_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month_short" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip 
                    formatter={(value, name) => [formatCurrency(value), name === 'revenue' ? 'Revenue' : name === 'profit' ? 'Profit' : name]}
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '2px', fontSize: '12px' }}
                  />
                  <Bar dataKey="revenue" fill="#C68E5D" radius={[2, 2, 0, 0]} name="Revenue" />
                  <Bar dataKey="profit" fill="#1A2F23" radius={[2, 2, 0, 0]} name="Profit" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex flex-col items-center justify-center text-muted-foreground">
                <BarChart3 className="w-8 h-8 mb-2 opacity-30" />
                <p className="text-sm">No sold deals yet. Revenue and profit will appear here as you close deals.</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Avg Days by Strategy */}
      {pipelineInsights?.avg_days_by_strategy?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pipelineInsights.avg_days_by_strategy.map((s) => (
            <Card key={s.strategy} className="rounded-sm border" data-testid={`strategy-stat-${s.strategy.toLowerCase()}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <Badge className="bg-forest-50 text-forest-700 border-forest-200 rounded-sm text-xs">{s.strategy}</Badge>
                  <span className="text-xs text-muted-foreground">{s.deals_closed} deals closed</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Avg Days to Close</p>
                    <p className="text-xl font-mono font-bold">{s.avg_days}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Avg Profit/Deal</p>
                    <p className="text-xl font-mono font-bold text-emerald-600">{formatCurrency(s.avg_profit)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Main Content Grid */}

      {/* Capital Forecast */}
      {forecast && (
        <Card className="rounded-sm border" data-testid="capital-forecast">
          <CardHeader className="pb-2">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <CardTitle className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-blue-500" />
                  Capital Forecast — 6 Month Projection
                </CardTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  Projected cash inflows and outflows based on pipeline deals, notes, and employee draws
                </p>
              </div>
              <div className="flex items-center gap-4 text-xs flex-wrap">
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-emerald-500 rounded-sm inline-block" /> Inflows</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-red-400 rounded-sm inline-block" /> LF Outflows</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-amber-500 rounded-sm inline-block" /> Investor Outflows</span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div className="p-3 sm:p-4 bg-white rounded-sm border" data-testid="forecast-total-inflows">
                <p className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground font-medium">Total Inflows</p>
                <p className="text-lg sm:text-2xl font-heading font-bold text-emerald-600 mt-1">
                  {formatCurrency(forecast.summary.total_projected_inflows)}
                </p>
                <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">Sales + Notes</p>
              </div>
              <div className="p-3 sm:p-4 bg-white rounded-sm border" data-testid="forecast-total-outflows">
                <p className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground font-medium">Total Outflows</p>
                <p className="text-lg sm:text-2xl font-heading font-bold text-red-600 mt-1">
                  {formatCurrency(forecast.summary.total_projected_outflows)}
                </p>
                <div className="flex gap-3 mt-1 text-[10px] sm:text-xs">
                  <span className="text-red-500">LF: {formatCurrency(forecast.summary.total_lf_outflows)}</span>
                  {forecast.summary.total_investor_outflows > 0 && (
                    <span className="text-red-400">Inv: {formatCurrency(forecast.summary.total_investor_outflows)}</span>
                  )}
                </div>
              </div>
              <div className="p-3 sm:p-4 bg-white rounded-sm border" data-testid="forecast-net">
                <p className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground font-medium">Net Projection</p>
                <p className={`text-lg sm:text-2xl font-heading font-bold mt-1 ${forecast.summary.net_projection >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {formatCurrency(Math.abs(forecast.summary.net_projection))}
                </p>
                <p className={`text-[10px] sm:text-xs mt-1 ${forecast.summary.net_projection >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {forecast.summary.net_projection >= 0 ? 'Net positive' : 'Net negative'}
                </p>
              </div>
              <div className="p-3 sm:p-4 bg-slate-50 rounded-sm border border-slate-200" data-testid="forecast-recurring">
                <p className="text-xs uppercase tracking-wide text-slate-700 font-medium">Monthly Recurring</p>
                <div className="mt-1 space-y-1">
                  <p className="text-sm font-mono">
                    <span className="text-emerald-600">+{formatCurrency(forecast.summary.monthly_note_income)}</span>
                    <span className="text-muted-foreground text-xs ml-1">notes</span>
                  </p>
                  <p className="text-sm font-mono">
                    <span className="text-red-500">-{formatCurrency(forecast.summary.monthly_employee_draws)}</span>
                    <span className="text-muted-foreground text-xs ml-1">draws</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Monthly Chart */}
            {forecast.forecast_months?.length > 0 && (
              <div className="mb-6">
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={forecast.forecast_months} barGap={0}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                    <YAxis 
                      tick={{ fontSize: 11 }} 
                      tickLine={false} 
                      axisLine={false}
                      tickFormatter={(v) => v >= 1000 ? `$${(v/1000).toFixed(0)}k` : `$${v}`} 
                    />
                    <Tooltip 
                      formatter={(value, name) => {
                        const labels = {
                          inflows: 'Inflows',
                          lf_outflows: 'Land Finder Outflows',
                          investor_outflows: 'Investor Outflows',
                        };
                        return [formatCurrency(value), labels[name] || name];
                      }}
                      labelStyle={{ fontWeight: 600 }}
                      contentStyle={{ borderRadius: '4px', fontSize: '12px' }}
                    />
                    <Bar dataKey="inflows" name="inflows" fill="#10B981" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="lf_outflows" name="lf_outflows" stackId="outflows" fill="#F87171" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="investor_outflows" name="investor_outflows" stackId="outflows" fill="#F59E0B" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Pipeline Detail Tables */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Incoming Deals */}
              <div>
                <h3 className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-2 flex items-center gap-1.5">
                  <DollarSign className="w-3.5 h-3.5 text-emerald-500" /> Expected Cash Inflows
                </h3>
                {forecast.pending_sell_deals?.length > 0 ? (
                  <div className="border rounded-sm overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Deal</th>
                          <th>Sale Price</th>
                          <th>Est. Close</th>
                        </tr>
                      </thead>
                      <tbody>
                        {forecast.pending_sell_deals.map((deal) => (
                          <tr key={deal.id}>
                            <td>
                              <Link to={`/projects/${deal.id}`} className="font-medium hover:text-primary text-sm">
                                {deal.name}
                              </Link>
                            </td>
                            <td className="font-mono text-emerald-600 text-sm">{formatCurrency(deal.amount)}</td>
                            <td className="text-sm">
                              {deal.estimated_close_date}
                              {!deal.has_estimated_date && (
                                <span className="text-xs text-amber-500 ml-1" title="No estimated close date set — using 30-day default">*</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground py-3">No deals under contract to sell</p>
                )}
                {forecast.active_notes?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-muted-foreground mb-1 font-medium">Active Notes ({forecast.active_notes.length})</p>
                    <div className="border rounded-sm overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Buyer</th>
                            <th>Monthly</th>
                            <th>Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {forecast.active_notes.map((note) => (
                            <tr key={note.id}>
                              <td className="text-sm">{note.buyer_name}</td>
                              <td className="font-mono text-emerald-600 text-sm">{formatCurrency(note.monthly_payment)}</td>
                              <td className="font-mono text-sm">{formatCurrency(note.remaining_balance)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* Outgoing */}
              <div>
                <h3 className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-2 flex items-center gap-1.5">
                  <Wallet className="w-3.5 h-3.5 text-red-400" /> Expected Outflows
                </h3>
                {forecast.pending_buy_deals?.length > 0 ? (
                  <div className="border rounded-sm overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                    <table className="data-table min-w-[450px]">
                      <thead>
                        <tr>
                          <th>Deal</th>
                          <th>Total</th>
                          <th>LF</th>
                          <th>Investor</th>
                          <th>Est. Close</th>
                        </tr>
                      </thead>
                      <tbody>
                        {forecast.pending_buy_deals.map((deal) => (
                          <tr key={deal.id}>
                            <td>
                              <Link to={`/projects/${deal.id}`} className="font-medium hover:text-primary text-sm">
                                {deal.name}
                              </Link>
                              {deal.has_jv && (
                                <span className="text-[10px] text-amber-600 ml-1.5 font-medium">JV</span>
                              )}
                            </td>
                            <td className="font-mono text-red-500 text-sm">{formatCurrency(deal.amount)}</td>
                            <td className="font-mono text-red-400 text-sm">{formatCurrency(deal.lf_amount)}</td>
                            <td className="font-mono text-amber-600 text-sm">
                              {deal.investor_amount > 0 ? formatCurrency(deal.investor_amount) : '—'}
                            </td>
                            <td className="text-sm">
                              {deal.estimated_close_date}
                              {!deal.has_estimated_date && (
                                <span className="text-xs text-amber-500 ml-1" title="No estimated close date set — using 30-day default">*</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground py-3">No deals under contract to buy</p>
                )}
                {forecast.employee_draws?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-muted-foreground mb-1 font-medium">Monthly Employee Draws</p>
                    <div className="border rounded-sm overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Employee</th>
                            <th>Monthly Draw</th>
                          </tr>
                        </thead>
                        <tbody>
                          {forecast.employee_draws.map((emp, i) => (
                            <tr key={i}>
                              <td className="text-sm">{emp.name}</td>
                              <td className="font-mono text-red-500 text-sm">{formatCurrency(emp.monthly_draw)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footnote for estimated dates */}
            {(forecast.pending_sell_deals?.some(d => !d.has_estimated_date) || forecast.pending_buy_deals?.some(d => !d.has_estimated_date)) && (
              <p className="text-xs text-amber-600 mt-4">
                * Deals without an estimated close date use a 30-day default. Set the "Estimated Close Date" on the project to improve accuracy.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* JV Capital Aging */}
      <Card className="rounded-sm border" data-testid="inventory-aging">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                JV Capital Aging
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">Investor capital deployment duration &amp; waterfall tier tracking</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {analytics?.inventory_aging?.length > 0 ? (
            <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0" style={{ WebkitOverflowScrolling: 'touch' }}>
              <table className="data-table min-w-[700px]">
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Investor</th>
                    <th>Capital Deployed</th>
                    <th>Days Held</th>
                    <th>Current Tier</th>
                    <th>Investor Split</th>
                    <th>Next Tier In</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.inventory_aging.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <Link to={`/projects/${item.id}`} className="font-medium hover:text-primary">
                          {item.name}
                        </Link>
                      </td>
                      <td className="text-sm">{item.investor_name}</td>
                      <td className="font-mono">{formatCurrency(item.investor_capital)}</td>
                      <td className={`font-mono ${item.current_tier >= 4 ? 'text-red-600 font-bold' : item.current_tier >= 3 ? 'text-amber-600 font-semibold' : ''}`}>
                        {item.days_held} days
                      </td>
                      <td>
                        <Badge 
                          variant="outline" 
                          className={`rounded-sm text-xs ${
                            item.current_tier >= 5 ? 'bg-red-100 text-red-700 border-red-300' :
                            item.current_tier >= 4 ? 'bg-orange-100 text-orange-700 border-orange-300' :
                            item.current_tier >= 3 ? 'bg-amber-100 text-amber-700 border-amber-300' :
                            'bg-emerald-100 text-emerald-700 border-emerald-300'
                          }`}
                        >
                          Tier {item.current_tier}
                        </Badge>
                      </td>
                      <td className="font-mono text-sm">{item.investor_split}%</td>
                      <td className="text-sm">
                        {item.days_to_next_tier != null ? (
                          <span className={item.days_to_next_tier <= 30 ? 'text-red-600 font-semibold' : item.days_to_next_tier <= 60 ? 'text-amber-600' : ''}>
                            {item.days_to_next_tier} days → {item.next_tier_split}%
                          </span>
                        ) : (
                          <span className="text-muted-foreground">Max tier</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              No JV deals currently held. This tracks aging for owned properties with investor agreements.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Clickable cost basis row
const COLUMN_LABELS = {
  jv_capital: 'JV Capital',
  purchase_price: 'Purchase Price',
  expenses: 'Expenses',
  emd: 'EMD',
  reimbursements: 'Reimbursements',
};

function CostBasisRow({ label, amount, negative, onClick, ...props }) {
  return (
    <button
      type="button"
      className={`flex justify-between items-center w-full text-sm py-1.5 px-1 -mx-1 rounded-sm transition-colors hover:bg-muted/60 cursor-pointer group ${negative ? 'text-red-600' : ''}`}
      onClick={onClick}
      {...props}
    >
      <span className="text-muted-foreground group-hover:text-foreground flex items-center gap-1">
        {label}
        <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
      </span>
      <span className="font-mono">
        {negative ? `(${formatCurrency(amount)})` : formatCurrency(amount)}
      </span>
    </button>
  );
}

function CostBasisDrillDown({ open, onOpenChange, data }) {
  if (!data) return null;
  const { title, projects, columns, color, showParty } = data;

  const colorClasses = {
    blue: 'text-blue-700 border-blue-200 bg-blue-50',
    terracotta: 'text-terracotta-700 border-terracotta-200 bg-terracotta-50',
    forest: 'text-forest-700 border-forest-200 bg-forest-50',
    red: 'text-red-700 border-red-200 bg-red-50',
  };
  const headerBg = colorClasses[color] || colorClasses.forest;

  const grandTotal = projects.reduce((sum, p) => sum + (columns.includes('reimbursements') ? (p.reimbursements || 0) : (p.total_contributed || 0)), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="cost-basis-drilldown">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold uppercase tracking-wide">
            {title}
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            {projects.length} project{projects.length !== 1 ? 's' : ''} contributing to this total
          </p>
        </DialogHeader>

        {projects.length === 0 ? (
          <p className="py-6 text-center text-muted-foreground text-sm">No project-level data for this category.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b-2 ${headerBg}`}>
                  <th className="text-left py-2 px-2 text-xs uppercase">Project</th>
                  {showParty && <th className="text-left py-2 px-2 text-xs uppercase">Party</th>}
                  {columns.map(col => (
                    <th key={col} className="text-right py-2 px-2 text-xs uppercase">{COLUMN_LABELS[col] || col}</th>
                  ))}
                  <th className="text-right py-2 px-2 text-xs uppercase font-bold">
                    {columns.includes('reimbursements') ? 'Amount' : 'Net'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {projects.map((p, i) => (
                  <tr key={`${p.project_id}-${i}`} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="py-2 px-2">
                      <Link
                        to={`/projects/${p.project_id}`}
                        className="font-medium text-sm hover:underline text-foreground"
                        onClick={() => onOpenChange(false)}
                      >
                        {p.project_name}
                      </Link>
                      {p.status && (
                        <span className="ml-1.5 text-[10px] text-muted-foreground">{p.status.replace('_', ' ')}</span>
                      )}
                    </td>
                    {showParty && (
                      <td className="py-2 px-2">
                        <Badge variant="outline" className="rounded-sm text-[10px]">{p.party}</Badge>
                      </td>
                    )}
                    {columns.map(col => (
                      <td key={col} className="py-2 px-2 text-right font-mono text-xs text-muted-foreground">
                        {(p[col] || 0) > 0 ? formatCurrency(p[col]) : '-'}
                      </td>
                    ))}
                    <td className={`py-2 px-2 text-right font-mono font-medium ${columns.includes('reimbursements') ? 'text-red-600' : ''}`}>
                      {formatCurrency(columns.includes('reimbursements') ? (p.reimbursements || 0) : (p.total_contributed || 0))}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className={`border-t-2 font-semibold ${headerBg}`}>
                  <td className="py-2 px-2 uppercase text-xs" colSpan={showParty ? 2 : 1}>Total</td>
                  {columns.map(col => (
                    <td key={col} className="py-2 px-2 text-right font-mono text-xs">
                      {formatCurrency(projects.reduce((s, p) => s + (p[col] || 0), 0))}
                    </td>
                  ))}
                  <td className="py-2 px-2 text-right font-mono">
                    {formatCurrency(grandTotal)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


// ── Deal Pipeline Funnel Widget ────────────────────────────────────
function DealPipelineFunnel({ data }) {
  const max = data.total_promoted || 1;
  return (
    <Card className="rounded-sm border" data-testid="deal-pipeline-funnel">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Deal Pipeline
          </CardTitle>
          <Link to="/partners">
            <Button variant="outline" size="sm" className="rounded-sm text-xs">
              Manage <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </Link>
        </div>
        <p className="text-xs text-muted-foreground">
          Conversion funnel from promoted deal → signed contract
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-2.5">
          {data.stages.map((stage, idx) => {
            const widthPct = Math.max((stage.count / max) * 100, 4);
            const colors = [
              'bg-blue-500',
              'bg-cyan-500',
              'bg-emerald-500',
              'bg-amber-500',
              'bg-orange-500',
              'bg-green-600',
            ];
            const cls = colors[idx % colors.length];
            return (
              <div key={stage.key} className="flex items-center gap-3">
                <div className="w-32 sm:w-40 flex-shrink-0 text-xs font-medium text-foreground/80">
                  {stage.label}
                </div>
                <div className="flex-1 relative h-7 bg-muted/40 rounded-sm overflow-hidden">
                  <div
                    className={`absolute inset-y-0 left-0 ${cls} transition-all duration-500 flex items-center justify-end px-2`}
                    style={{ width: `${widthPct}%` }}
                  >
                    <span className="text-xs font-bold text-white drop-shadow">{stage.count}</span>
                  </div>
                </div>
                <div className="w-24 sm:w-32 flex-shrink-0 text-right text-[11px] text-muted-foreground">
                  {stage.conversion_from_prev != null ? (
                    <>
                      <span className={`font-medium ${stage.conversion_from_prev >= 50 ? 'text-emerald-600' : stage.conversion_from_prev >= 20 ? 'text-amber-600' : 'text-red-500'}`}>
                        {stage.conversion_from_prev}%
                      </span>
                      <span className="ml-1 opacity-60">from prev</span>
                    </>
                  ) : (
                    <span className="opacity-60">—</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-4 pt-3 border-t flex items-center justify-between text-xs text-muted-foreground">
          <span>{data.total_promoted} total promoted</span>
          {(() => {
            const signed = data.stages.find(s => s.key === 'signed')?.count || 0;
            const overall = data.total_promoted ? Math.round((signed / data.total_promoted) * 1000) / 10 : 0;
            return (
              <span>
                Overall conversion: <span className="font-semibold text-foreground">{overall}%</span>
              </span>
            );
          })()}
        </div>
      </CardContent>
    </Card>
  );
}

